import { type User, type UserRole, UserRoles } from '@engagement-web-app/common';
import { numberSchema, stringSchema } from '@engagement-web-app/common/validation-utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Navigate, stripSearchParams, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';
import { useClientAuthentication } from '@/auth/ClientAuthenticationProvider';
import { Button } from '@/components/Button';
import { DeleteUserDialog } from '@/components/DeleteUserDialog';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { InvalidRequest } from '@/components/InvalidRequest';
import { MfaResetDialog } from '@/components/MfaResetDialog';
import { Page } from '@/components/Page';
import { Pagination } from '@/components/Pagination';
import { Pill } from '@/components/Pill';
import { ResetPasswordDialog } from '@/components/ResetPasswordDialog';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { UserCard, UserCardSkeleton } from '@/components/UserCard';
import { type AddUserFormData, type EditUserFormData, UserFormDialog } from '@/components/UserFormDialog';
import { hasClientPermission } from '@/permissions/clientUserPermissions';
import { fetchUserRoles } from '@/queries/user-roles/client';
import { createUser, deleteUser, fetchUsers, resetUserMfa, resetUserPassword, updateUser } from '@/queries/users';
import { debounce, getFullName } from '@/utils';
import * as styles from './index.css';

const FROM = 0;
const LIMIT = 6;
const SEARCH = '';

const UserListRequestQuery = z.object({
    from: numberSchema.default(FROM),
    limit: numberSchema.max(100).default(LIMIT),
    search: stringSchema.default(SEARCH),
});

export const Route = createFileRoute('/_client/_auth/users/')({
    component: UserListRoute,
    validateSearch: zodValidator(UserListRequestQuery),
    search: {
        middlewares: [stripSearchParams({ from: FROM, limit: LIMIT, search: SEARCH })],
    },
    errorComponent: () => <InvalidRequest title="ユーザー" kind="client" />,
});

// User management is admin-only. `currentUser` is already provided by the layout
// (see routes/_client/_auth/route.tsx), so this only exists to gate `UserList`'s
// queries from firing at all for a member about to be redirected away.
function UserListRoute() {
    const currentUser = useClientAuthentication();

    if (!hasClientPermission(currentUser.role.id, 'users', 'view')) {
        return <Navigate to="/" />;
    }

    return <UserList currentUser={currentUser} />;
}

function UserList({ currentUser }: { currentUser: User }) {
    const { from = FROM, limit = LIMIT, search = SEARCH } = Route.useSearch();
    const [lastPage, setLastPage] = useState<number | null>(null);
    const navigate = Route.useNavigate();
    const queryClient = useQueryClient();
    const [searchText, setSearchText] = useState(search);
    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            navigate({
                from: Route.fullPath,
                search: { from: 0, search },
            });
        }, 300),
        []
    );

    const { isFetching, isError, refetch, data } = useQuery({
        queryKey: ['users', from, limit, search],
        queryFn: async () => {
            const data = await fetchUsers({ from, limit, search });
            const lastPage = Math.ceil(data.total / limit);
            setLastPage(lastPage);
            return data;
        },
        placeholderData: keepPreviousData,
    });

    const { data: userRoles } = useQuery({
        queryKey: ['userRoles'],
        queryFn: () => fetchUserRoles(),
    });

    const createMutation = useMutation({
        mutationFn: createUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            showSuccessToast({ title: 'ユーザー追加', description: 'ユーザーが追加されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'ユーザー追加', description: 'ユーザーの追加に失敗しました。' });
        },
    });

    const handlePageChange = (page: number) => {
        navigate({
            from: Route.fullPath,
            search: { from: (page - 1) * limit },
        });
    };

    return (
        <Page.Root>
            <Page.Header title="ユーザー">
                <Pill>{`${data?.total ?? 0}人`}</Pill>
            </Page.Header>

            <Page.Content>
                <div className={styles.searchRow}>
                    <div className={styles.searchContainer}>
                        <SearchInput
                            placeholder="検索"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                debouncedSearch(e.target.value);
                            }}
                        />
                    </div>
                    <Button variant="primary" onClick={() => setIsAddUserDialogOpen(true)}>
                        <Icon icon={Plus} />
                        ユーザーを追加
                    </Button>
                </div>

                <UserFormDialog
                    mode="add"
                    userRoles={userRoles ?? []}
                    isOpen={isAddUserDialogOpen}
                    onOpenChange={setIsAddUserDialogOpen}
                    isSubmitting={createMutation.isPending}
                    onSubmit={async (data: AddUserFormData) => {
                        await createMutation.mutateAsync(data);
                        setIsAddUserDialogOpen(false);
                    }}
                />

                <ErrorOverlay active={isError} onRetry={refetch}>
                    {!data && <UserCardListSkeleton />}

                    {data && (
                        <>
                            <p className={styles.countText}>ユーザー数：{data.count}人</p>

                            <Skeleton.Overlay active={isFetching}>
                                <UserCardList
                                    list={data.list}
                                    userRoles={userRoles ?? []}
                                    currentUserId={currentUser.uuid}
                                />
                            </Skeleton.Overlay>
                        </>
                    )}
                </ErrorOverlay>

                {lastPage && (
                    <Flex justify="center">
                        <Pagination
                            currentPage={from / limit + 1}
                            lastPage={lastPage}
                            onPageChange={handlePageChange}
                        />
                    </Flex>
                )}
            </Page.Content>
        </Page.Root>
    );
}

interface UserCardListProps {
    list: User[];
    userRoles: UserRole[];
    currentUserId?: string;
}

function UserCardList({ list, userRoles, currentUserId }: UserCardListProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ uuid: string; name: string } | null>(null);
    const [resetPasswordTarget, setResetPasswordTarget] = useState<{
        uuid: string;
        name: string;
        email: string;
    } | null>(null);
    const [mfaResetTarget, setMfaResetTarget] = useState<{ uuid: string; name: string } | null>(null);

    const updateMutation = useMutation({
        mutationFn: ({ uuid, body }: { uuid: string; body: EditUserFormData }) => updateUser(uuid, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            showSuccessToast({ title: 'ユーザー編集', description: 'ユーザー情報が更新されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'ユーザー編集', description: 'ユーザー情報の更新に失敗しました。' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: ({ uuid }: { uuid: string }) => deleteUser(uuid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setDeleteTarget(null);
            showSuccessToast({ title: 'ユーザー削除', description: 'ユーザーが削除されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'ユーザー削除', description: 'ユーザーの削除に失敗しました。' });
        },
    });

    const resetPasswordMutation = useMutation({
        mutationFn: ({ uuid }: { uuid: string }) => resetUserPassword(uuid),
        onSuccess: () => {
            setResetPasswordTarget(null);
            showSuccessToast({
                title: 'パスワードリセット',
                description: 'パスワード再設定メールを送信しました。',
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({
                title: 'パスワードリセット',
                description: 'パスワード再設定メールの送信に失敗しました。',
            });
        },
    });

    const resetMfaMutation = useMutation({
        mutationFn: ({ uuid }: { uuid: string }) => resetUserMfa(uuid),
        onSuccess: () => {
            setMfaResetTarget(null);
            showSuccessToast({
                title: 'MFAリセット',
                description: 'MFAをリセットしました。',
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({
                title: 'MFAリセット',
                description: 'MFAのリセットに失敗しました。',
            });
        },
    });

    const handleEditUser = async (data: EditUserFormData) => {
        if (!editingUser) return;
        await updateMutation.mutateAsync({ uuid: editingUser.uuid, body: data });
        setEditingUser(null);
    };

    const handleResetPassword = async () => {
        if (!resetPasswordTarget) return;
        await resetPasswordMutation.mutateAsync({ uuid: resetPasswordTarget.uuid });
    };

    const handleResetMfa = async () => {
        if (!mfaResetTarget) return;
        await resetMfaMutation.mutateAsync({ uuid: mfaResetTarget.uuid });
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        await deleteMutation.mutateAsync({ uuid: deleteTarget.uuid });
        setDeleteTarget(null);
    };

    if (list.length === 0) {
        return (
            <div>
                <p className={styles.emptyState}>検索条件に一致するユーザーはいません。</p>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.cardGrid}>
                {list.map((user: User) => (
                    <UserCard
                        key={user.uuid}
                        name={getFullName(user)}
                        email={user.email}
                        phoneNumber={user.phoneNumber}
                        role={user.role.displayName}
                        mode="client"
                        hideDelete={user.uuid === currentUserId}
                        onEdit={() => setEditingUser(user)}
                        // Admins are never narrowed by per-survey permissions.
                        onEditPermissions={
                            user.role.id === UserRoles.enum.MEMBER
                                ? () =>
                                      navigate({
                                          to: '/users/$userId/survey-permissions',
                                          params: { userId: user.uuid },
                                      })
                                : undefined
                        }
                        onResetPassword={() => {
                            setResetPasswordTarget({
                                uuid: user.uuid,
                                name: getFullName(user),
                                email: user.email,
                            });
                        }}
                        onResetMfa={() => {
                            setMfaResetTarget({
                                uuid: user.uuid,
                                name: getFullName(user),
                            });
                        }}
                        onDelete={() => {
                            setDeleteTarget({
                                uuid: user.uuid,
                                name: getFullName(user),
                            });
                        }}
                    />
                ))}
            </div>

            <DeleteUserDialog
                target={deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                isSubmitting={deleteMutation.isPending}
                onSubmit={handleDeleteUser}
            />

            <ResetPasswordDialog
                target={resetPasswordTarget}
                onOpenChange={(open) => !open && setResetPasswordTarget(null)}
                isSubmitting={resetPasswordMutation.isPending}
                onSubmit={handleResetPassword}
            />

            <MfaResetDialog
                target={mfaResetTarget}
                onOpenChange={(open) => !open && setMfaResetTarget(null)}
                isSubmitting={resetMfaMutation.isPending}
                onSubmit={handleResetMfa}
            />

            <UserFormDialog
                mode="edit"
                userRoles={userRoles}
                user={editingUser}
                isOpen={editingUser !== null}
                onOpenChange={(open) => !open && setEditingUser(null)}
                isSubmitting={updateMutation.isPending}
                onSubmit={handleEditUser}
            />
        </div>
    );
}

function UserCardListSkeleton() {
    return (
        <>
            <Skeleton.Line width={100} height="xs" />

            <div className={styles.cardGrid}>
                {Array.from({ length: LIMIT }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, cards have no identity
                    <UserCardSkeleton key={i} />
                ))}
            </div>
        </>
    );
}
